// popover.js and embed.js start Pixie on a recipe page whose site has her
// switched on, so the site needs no pixie.js tag of its own. They ask the API
// which site's Pixie the page's recipe and origin belong to, and add pixie.js
// for that site. A page that has pixie.js already, or no recipe id, asks nothing.
import ExperienceAttributes, {
  attrNames,
} from "@/modules/ExperienceAttributes";
import { isRecipeId } from "@/modules/recipeId";

// pix-arcs-api's custom MCP slug rule (app/mcp_servers/slugs.py).
const SLUG = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])$/;
const PIXIE_SRC = /\/pixie\.js(?:[?#]|$)/;

/**
 * pixie.js has run, or its tag is on the page and has not run yet. A tag
 * counts only when it names a site, as pixie.js's own `findScript` does: a
 * site's unrelated `pixie.js` must not keep Pixie away, and a tag without
 * `data-publisher` shows nothing.
 */
function pixieOnPage() {
  if (window.Pixie) {
    return true;
  }
  const scripts = document.getElementsByTagName("script");
  for (let i = 0; i < scripts.length; i++) {
    if (
      PIXIE_SRC.test(scripts[i].src || "") &&
      scripts[i].hasAttribute("data-publisher")
    ) {
      return true;
    }
  }
  return false;
}

// The element's own attributes only: a `?pix-expid=` in the page URL is not
// the page's recipe.
function firstRecipe(elements: ArrayLike<Element>) {
  for (let i = 0; i < elements.length; i++) {
    const attrs = ExperienceAttributes.attrsFromElement(elements[i]);
    const id = attrs[attrNames.EXPERIENCE_ID];
    if (typeof id === "string" && isRecipeId(id.trim())) {
      return { id: id.trim(), attrs: new ExperienceAttributes(attrs) };
    }
  }
  return null;
}

function lookupUrl(experienceId: string, attrs: ExperienceAttributes) {
  const pm = window.__pixmoto || {};
  return (
    `${pm.apiProtocol || "https"}://${pm.apiBaseUrl || attrs.getApiBaseHost()}` +
    `/publisher/assistant/widget/lookup?experience_id=${encodeURIComponent(experienceId)}`
  );
}

function addPixie(playOrigin: string, publisher: string, experienceId: string) {
  const script = document.createElement("script");
  script.src = `${playOrigin}/pixie.js`;
  script.async = true;
  script.setAttribute("data-publisher", publisher);
  script.setAttribute("data-expid", experienceId);
  script.setAttribute("data-position", "bottom");
  (document.head || document.documentElement).appendChild(script);
}

/**
 * `elements` are the page's popover anchors or embeds; `playOrigin` served the
 * running script (`hostScriptOrigin`), and pixie.js comes from there too. One
 * lookup per page, shared by popover.js and embed.js.
 */
export function startPixieForRecipes(
  elements: ArrayLike<Element>,
  playOrigin: string,
) {
  // pixie.js needs shadow DOM; without it there is nothing to load.
  if (
    pixieOnPage() ||
    typeof fetch !== "function" ||
    typeof document.documentElement.attachShadow !== "function"
  ) {
    return;
  }
  const pm = window.__pixmoto || {};
  window.__pixmoto = pm;
  if (pm._pixieLookup) {
    return;
  }
  const recipe = firstRecipe(elements);
  // Pixmoto's own pages (samples, previews) are no site's.
  if (!recipe || playOrigin === window.location.origin) {
    return;
  }
  pm._pixieLookup = true;
  fetch(lookupUrl(recipe.id, recipe.attrs), {
    credentials: "omit",
    mode: "cors",
  })
    .then(response => (response.ok ? response.json() : null))
    .then((body: { publisher?: unknown } | null) => {
      const publisher = body && body.publisher;
      // The page may have added its own tag meanwhile: one Pixie per page.
      if (
        typeof publisher === "string" &&
        SLUG.test(publisher) &&
        !pixieOnPage()
      ) {
        addPixie(playOrigin, publisher, recipe.id);
      }
    })
    .catch(() => {
      /* no Pixie: the page plays its recipes as before */
    });
}
